import type { DeckorateCommand } from "../../../types";
import { logError, logEvent, scrubMessage } from "../../../../../telemetry";

/** Matches CommandPalette.tsx's/runAction.ts's connectorSystemName. */
const CMD_PALETTE_CONNECTOR = "deckorate";

function logOpenDialogError(reason: string, type: string, e: unknown): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(e instanceof Error ? e.message : String(e)) },
    { connectorSystemName: CMD_PALETTE_CONNECTOR, reason },
  );
}

/** Office Dialog window size as a PERCENTAGE of the screen (the API only
 * accepts percentages) — sized to frame the 440px palette card plus scrim
 * margin, smaller than openCreateSlidesDialog.ts's wider wizard card. */
const DIALOG_SIZE = { height: 60, width: 45 } as const;

/** Relative path of the dialog page served from the add-in origin. */
const DIALOG_PAGE = "/all-actions-dialog.html";

/** Whether the Office Dialog API is available (i.e. running inside a host). */
export function canUseDialog(): boolean {
  return typeof Office !== "undefined" && typeof Office.context?.ui?.displayDialogAsync === "function";
}

/**
 * Opens "All actions" as an Office Dialog. The dialog only searches the
 * catalog — "pending"/"ai-preview" commands resolve entirely inside it, while
 * runnable commands are posted back here for the task pane to execute.
 *
 * Resolves with the selected runnable command, or null if the user cancels.
 * Rejects (with a user-facing message) if the dialog can't be opened at all.
 */
export function openAllActionsDialog(): Promise<DeckorateCommand | null> {
  return new Promise((resolve, reject) => {
    if (!canUseDialog()) {
      resolve(null);
      return;
    }
    const url = `${globalThis.location.origin}${DIALOG_PAGE}`;
    Office.context.ui.displayDialogAsync(url, { ...DIALOG_SIZE }, (asyncResult) => {
      if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
        const err = asyncResult.error;
        logOpenDialogError(
          "cmd-palette-dialog-launch-failed",
          "DisplayDialogAsyncError",
          new Error(`Office failed to open the All actions dialog (code ${err?.code ?? "?"}): ${err?.message ?? "unknown error"}`),
        );
        reject(new Error("Couldn't open the All actions window. Please try again."));
        return;
      }
      const dialog = asyncResult.value;
      let settled = false;
      const settle = (value: DeckorateCommand | null) => {
        if (settled) return;
        settled = true;
        try {
          dialog.close();
        } catch {
          // close() throws if the host already tore the dialog down (e.g. the
          // user closed it as settle() was called) — not an error, just a breadcrumb.
          logEvent("connector.action.invoke", "info", {
            connectorSystemName: CMD_PALETTE_CONNECTOR,
            reason: "cmd-palette-dialog-already-closed",
          });
        }
        resolve(value);
      };
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: unknown) => {
        try {
          const { message } = arg as { message: string };
          const parsed = JSON.parse(message) as { type?: string; command?: DeckorateCommand };
          settle(parsed.type === "run" && parsed.command ? parsed.command : null);
        } catch (e) {
          logOpenDialogError("cmd-palette-dialog-message-parse-failed", "DialogMessageParseError", e);
          settle(null);
        }
      });
      // User closed the dialog (X / host close) — resolve so we don't hang.
      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => settle(null));
    });
  });
}
